import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Minus, Plus, ArrowLeft, Pill, Repeat, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import PrescriptionPickerSheet from "../components/PrescriptionPickerSheet";
import SubscribeDialog from "../components/SubscribeDialog";
import ProductGallery from "../components/ProductGallery";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";

const ProductDetail = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [rxOpen, setRxOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const [subscribeOpen, setSubscribeOpen] = useState(false);
    const { user } = useAuth();
    const { addItem } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/products/${id}`)
            .then(({ data }) => { if (mounted) setProduct(data); })
            .catch(() => { if (mounted) setProduct(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [id]);

    const doAdd = async (prescriptionId = null) => {
        if (!product) return;
        try {
            setAdding(true);
            await addItem(product.id, qty, prescriptionId);
            toast.success("Added to cart");
            setRxOpen(false);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            const msg = typeof detail === "string" ? detail : detail?.message || "Could not add to cart";
            if (code === "PRESCRIPTION_NOT_READY") {
                toast.error("This prescription is still processing. Please wait or pick another.");
            } else {
                toast.error(msg);
            }
        } finally {
            setAdding(false);
        }
    };

    const handleAddToCart = async () => {
        if (!product) return;
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(`/products/${id}`)}`);
            return;
        }
        if (product.prescription_required) {
            setRxOpen(true);
            return;
        }
        await doAdd(null);
    };

    const onPickPrescription = async (p) => {
        await doAdd(p.id);
    };

    if (loading) {
        return (
            <Layout>
                <div className="mx-auto max-w-6xl px-6 py-16 lg:px-12">
                    <div className="grid gap-10 lg:grid-cols-2">
                        <div className="aspect-square skeleton rounded-3xl" />
                        <div className="space-y-4">
                            <div className="h-4 w-20 skeleton" />
                            <div className="h-8 w-3/4 skeleton" />
                            <div className="h-4 w-1/3 skeleton" />
                            <div className="h-10 w-1/2 skeleton" />
                            <div className="h-12 w-full skeleton" />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout>
                <div className="mx-auto max-w-3xl px-6 py-20 text-center">
                    <h1 className="text-2xl font-semibold tracking-tight">Product not found</h1>
                    <p className="mt-2 text-sm text-zinc-500">It may have been removed or never existed.</p>
                    <Link to="/products" className="btn-brand mt-6 inline-flex">Back to catalog</Link>
                </div>
            </Layout>
        );
    }

    const off = Math.round(((product.mrp - product.selling_price) / product.mrp) * 100);

    return (
        <Layout>
            <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12" data-testid="product-detail-page">
                <Link to="/products" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> Back to catalog
                </Link>

                <div className="grid gap-12 lg:grid-cols-2">
                    {/* Gallery */}
                    <div className="relative">
                        <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-brand-radial" />
                        <ProductGallery
                            images={product.images || []}
                            videos={product.videos || []}
                            productName={product.name}
                        />
                    </div>

                    {/* Info */}
                    <div className="space-y-5">
                        {product.brand && <p className="label-uppercase">{product.brand}</p>}
                        <div className="flex flex-wrap items-start gap-3">
                            <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
                                {product.name}
                            </h1>
                            {product.prescription_required && (
                                <span className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-brand-700" data-testid="detail-rx-badge">
                                    <ShieldCheck className="h-3.5 w-3.5" /> Rx Required
                                </span>
                            )}
                        </div>
                        {product.generic_name && (
                            <p className="text-sm text-zinc-500">Generic: {product.generic_name}</p>
                        )}
                        {product.pack_size && (
                            <p className="text-sm text-zinc-500">Pack: {product.pack_size}</p>
                        )}

                        <div className="flex items-baseline gap-3 pt-2">
                            <span className="text-3xl font-semibold text-zinc-900">₹{product.selling_price}</span>
                            {product.mrp > product.selling_price && (
                                <>
                                    <span className="text-base text-zinc-400 line-through">₹{product.mrp}</span>
                                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{off}% off</span>
                                </>
                            )}
                        </div>
                        {product.price_per_unit ? (
                            <p className="text-sm text-zinc-500" data-testid={`detail-per-unit-${product.id}`}>
                                <strong className="text-zinc-700">₹{product.price_per_unit.toFixed(2)}</strong> per tablet · {product.pack_count} tablets in pack
                            </p>
                        ) : null}

                        {product.short_description && (
                            <p className="leading-relaxed text-zinc-600">{product.short_description}</p>
                        )}

                        {!product.prescription_required && (
                            <div className="flex items-center gap-3 pt-2">
                                <div className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white p-1" data-testid="qty-stepper">
                                    <button
                                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                                        className="rounded-full p-2 hover:bg-zinc-50"
                                        aria-label="Decrement"
                                        data-testid="qty-decrement-btn"
                                    >
                                        <Minus className="h-4 w-4" />
                                    </button>
                                    <span className="min-w-[2rem] text-center text-sm font-medium" data-testid="qty-value">{qty}</span>
                                    <button
                                        onClick={() => setQty((q) => Math.min(99, q + 1))}
                                        className="rounded-full p-2 hover:bg-zinc-50"
                                        aria-label="Increment"
                                        data-testid="qty-increment-btn"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleAddToCart}
                            disabled={adding}
                            className={`mt-2 ${product.prescription_required ? "inline-flex items-center justify-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-6 py-3 text-sm font-medium text-brand-700 hover:bg-brand-100" : "btn-brand"} w-full`}
                            data-testid="add-to-cart-btn"
                        >
                            {product.prescription_required ? (
                                <>
                                    <ShieldCheck className="h-4 w-4" /> Attach prescription to add
                                </>
                            ) : adding ? "Adding…" : "Add to cart"}
                        </button>

                        <button
                            onClick={() => {
                                if (!user) {
                                    navigate(`/login?next=${encodeURIComponent(`/products/${id}`)}`);
                                    return;
                                }
                                setSubscribeOpen(true);
                            }}
                            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand-200 bg-white px-6 py-3 text-sm font-medium text-brand-700 transition-all hover:border-brand-300 hover:bg-brand-50"
                            data-testid="subscribe-monthly-btn"
                        >
                            <Repeat className="h-4 w-4" /> Subscribe monthly
                            <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-brand-600">
                                <CalendarClock className="h-3 w-3" /> Auto-refill
                            </span>
                        </button>

                        {product.stock_qty < 50 && product.stock_qty > 0 && (
                            <p className="text-xs text-amber-600">Only {product.stock_qty} left in stock</p>
                        )}

                        <ul className="grid grid-cols-2 gap-3 pt-4 text-xs text-zinc-600">
                            <li className="flex items-center gap-2"><Pill className="h-4 w-4 text-brand-700" /> Genuine medicines</li>
                            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-700" /> Verified by pharmacists</li>
                        </ul>
                    </div>
                </div>

                {/* Substitutes */}
                {product.substitutes_resolved?.length > 0 && (
                    <div className="mt-16" data-testid="substitutes-section">
                        <p className="label-uppercase">You may also consider</p>
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">Substitutes</h2>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {product.substitutes_resolved.map((s) => (
                                <Link
                                    to={`/products/${s.id}`}
                                    key={s.id}
                                    className="flex items-center justify-between rounded-2xl border border-zinc-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-brand"
                                    data-testid={`substitute-${s.id}`}
                                >
                                    <span className="text-sm font-medium text-zinc-900">{s.name}</span>
                                    <span className="text-sm font-semibold text-brand-700">₹{s.selling_price}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <PrescriptionPickerSheet open={rxOpen} onClose={() => setRxOpen(false)} onSelect={onPickPrescription} />
            <SubscribeDialog
                open={subscribeOpen}
                product={product}
                onClose={() => setSubscribeOpen(false)}
                onCreated={() => navigate("/subscriptions")}
            />
        </Layout>
    );
};

export default ProductDetail;
